/**
 * Quick create (Outlook/Google fast path): title, the selected time, calendar; Save or "More details" for the full
 * editor. Opened by typing on a selected slot, Enter, double-click or "New event" in the slot menu.
 */
import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, EntityListPicker, entityPickerLabelsFor, toast, useErrorText } from '@platform/ui';
import { useId, useState } from 'react';
import { api } from '../api';
import { useCalendar } from './calendar-context';
import { calendarName } from './events';
import { useCalendarFormat } from './format';
import { parseQuickAdd } from './quick-parse';

export interface QuickCreateRequest {
  start: Date;
  end: Date;
  allDay: boolean;
  /** Typed character(s) that opened it. */
  title: string;
}

export function QuickCreate({ request, onClose }: { request: QuickCreateRequest; onClose: () => void }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { calendars, changed, openEditor, settings, zone } = useCalendar();
  const titleId = useId();
  const writable = (calendars.data ?? []).filter((c) => c.canCreate);
  const [title, setTitle] = useState(request.title);
  const [calendarId, setCalendarId] = useState('own');
  const [saving, setSaving] = useState(false);

  // Natural language ("Lunch fri 1pm", "Team morgen 9-10"): a recognised day/time replaces the selected one.
  const parsed = parseQuickAdd(title, zone.toWall(new Date()), settings.defaultDurationMinutes);
  const when = parsed.start && parsed.end ? { start: parsed.start, end: parsed.end, allDay: parsed.allDay ?? false } : request;
  const subject = parsed.start ? parsed.title : title.trim();

  const save = async () => {
    if (!subject) return;
    setSaving(true);
    try {
      await api.createEvent(calendarId, {
        subject,
        start: zone.fromWall(when.start).toISOString(),
        end: zone.fromWall(when.end).toISOString(),
        allDay: when.allDay,
        reminderMinutes: settings.defaultReminderMinutes,
        timeZone: zone.zone,
      });
      toast.success(t('webmail.calendar.editor.savedToast'));
      changed();
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setSaving(false);
    }
  };

  const more = () => {
    onClose();
    openEditor({ mode: 'create', start: when.start, end: when.end, allDay: when.allDay, calendarId, title: subject });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendar.newEvent')}</DialogTitle>
          <DialogDescription aria-live="polite">{format.selection(when.start, when.end, when.allDay)}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={titleId}>{t('webmail.calendar.editor.title')}</Label>
            <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.quickAdd.hint')}</p>
            <Input id={titleId} value={title} autoFocus onFocus={(e) => e.currentTarget.setSelectionRange(title.length, title.length)} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {writable.length > 1 && (
            <EntityListPicker<string>
              label={t('webmail.calendar.editor.calendar')}
              value={calendarId}
              items={writable.map((c) => ({ id: c.id, label: calendarName(c, t), identity: calendarName(c, t) }))}
              labels={entityPickerLabelsFor(t)}
              onChange={(entry) => { if (entry) setCalendarId(entry.id); }}
            />
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={more}>
              {t('webmail.calendar.quick.more')}
            </Button>
            <Button type="submit" disabled={saving || !subject}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
